import React, { useMemo, useCallback, useRef, useState, useEffect, useId, useContext } from "react";
import { Menu, ConfigProvider, Button } from "antd";
import type { MenuProps as AntMenuProps } from "antd";
import * as Icons from "@ant-design/icons";
import { LeftOutlined, RightOutlined, UpOutlined, DownOutlined } from "@ant-design/icons";
import { useAndStore } from "../../base/store/useAndStore";
import { DataSetInheritContext } from "../../base/DataSetInheritContext";
import { getDataSetStoreKey } from "../../base/storeKeyUtils";
import { deriveScopedComponentId } from "../../base/storePropsUtils";
import { createCommand } from "../../base/actionParser";
import type { BaseComponentProps, DataSetConfig } from "../../types/common";

/**
 * 将 CSSProperties 对象转换为 CSS 字符串
 */
const cssPropertiesToString = (styles: React.CSSProperties): string => {
  return Object.entries(styles)
    .map(([key, value]) => {
      // 将 camelCase 转换为 kebab-case
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${cssKey}: ${value}`;
    })
    .join('; ');
};

/**
 * 溢出处理策略类型
 * - default: 使用 Ant Design 原生省略号
 * - pagination: 翻页模式（左右/上下箭头导航）
 * - scroll: 滚动条模式
 */
export type OverflowStrategy = "default" | "pagination" | "scroll";

/** 翻页箭头某一状态（如 disabled / hover）下的语义样式 */
export interface PaginationArrowStateStyles {
  root?: React.CSSProperties;
  icon?: React.CSSProperties;
}

/**
 * 翻页箭头语义样式（paginationArrowStyle 层级协议）
 * - root：按钮容器
 * - icon：箭头图标
 * - disabled / hover：对应状态下的 root + icon
 * 仍兼容顶层扁平 CSSProperties（历史配置）
 */
export interface PaginationArrowSemanticStyles {
  root?: React.CSSProperties;
  icon?: React.CSSProperties;
  content?: React.CSSProperties;
  disabled?: PaginationArrowStateStyles;
  hover?: PaginationArrowStateStyles;
}

export type PaginationArrowStyle =
  | React.CSSProperties
  | PaginationArrowSemanticStyles;

const PAGINATION_ARROW_SEMANTIC_KEYS = [
  "root",
  "icon",
  "content",
  "disabled",
  "hover",
] as const;

const normalizeCssProperties = (
  styles?: React.CSSProperties
): React.CSSProperties => {
  if (!styles) return {};
  const result = { ...styles };
  if (
    "background" in result &&
    result.background != null &&
    result.backgroundColor == null
  ) {
    result.backgroundColor = result.background as React.CSSProperties["backgroundColor"];
    delete result.background;
  }
  return result;
};

const isSemanticPaginationArrowStyle = (
  style?: PaginationArrowStyle
): style is PaginationArrowSemanticStyles => {
  if (!style || typeof style !== "object" || Array.isArray(style)) return false;
  return PAGINATION_ARROW_SEMANTIC_KEYS.some((key) => {
    const value = (style as Record<string, unknown>)[key];
    return (
      value !== undefined &&
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value)
    );
  });
};

const resolvePaginationArrowButtonStyle = (
  semantic: PaginationArrowSemanticStyles,
  disabled: boolean
): React.CSSProperties => {
  const root = normalizeCssProperties(semantic.root);
  const icon = semantic.icon ?? {};
  const merged: React.CSSProperties = {
    ...root,
    ...(icon.color != null ? { color: icon.color } : {}),
  };

  if (disabled && semantic.disabled) {
    Object.assign(merged, normalizeCssProperties(semantic.disabled.root));
    if (semantic.disabled.icon?.color != null) {
      merged.color = semantic.disabled.icon.color;
    }
  }

  return merged;
};

const resolvePaginationArrowIconStyle = (
  semantic: PaginationArrowSemanticStyles,
  disabled: boolean
): React.CSSProperties | undefined => {
  const iconStyles = disabled
    ? semantic.disabled?.icon ?? semantic.icon
    : semantic.icon;
  if (!iconStyles) return undefined;

  const { color: _color, ...rest } = iconStyles;
  return Object.keys(rest).length > 0 ? rest : undefined;
};

const buildPaginationArrowHoverCss = (
  semantic: PaginationArrowSemanticStyles,
  scopeId: string
): string => {
  const hover = semantic.hover;
  if (!hover) return "";

  const scope = `[data-and-menu-id="${scopeId}"]`;
  const selector = `${scope} .nuwa-menu-page-arrow:not(:disabled)`;
  const rules: string[] = [];

  if (hover.root && Object.keys(hover.root).length > 0) {
    rules.push(
      `${selector} { ${cssPropertiesToString(normalizeCssProperties(hover.root))} }`
    );
  }
  if (hover.icon && Object.keys(hover.icon).length > 0) {
    rules.push(
      `${selector} .nuwa-menu-page-arrow-icon { ${cssPropertiesToString(hover.icon)} }`
    );
  }

  return rules.join("\n");
};

const wrapPaginationArrowIcon = (
  icon: React.ReactNode,
  iconStyle?: React.CSSProperties
): React.ReactNode => {
  if (!iconStyle) return icon;
  return (
    <span className="nuwa-menu-page-arrow-icon" style={iconStyle}>
      {icon}
    </span>
  );
};

/**
 * 翻页容器组件 - 用于实现翻页模式的溢出处理
 */
const PaginationWrapper: React.FC<{
  mode: "horizontal" | "vertical" | "inline";
  arrowStyle?: PaginationArrowStyle;
  styleScopeId?: string;
  arrowIconPrev?: React.ReactNode;
  arrowIconNext?: React.ReactNode;
  children: React.ReactNode;
}> = ({
  mode,
  arrowStyle,
  styleScopeId,
  arrowIconPrev,
  arrowIconNext,
  children,
}) => {
  const semanticStyle = isSemanticPaginationArrowStyle(arrowStyle)
    ? arrowStyle
    : undefined;
  const flatArrowStyle = semanticStyle
    ? undefined
    : (arrowStyle as React.CSSProperties | undefined);
  const hoverCss = useMemo(
    () =>
      semanticStyle && styleScopeId
        ? buildPaginationArrowHoverCss(semanticStyle, styleScopeId)
        : "",
    [semanticStyle, styleScopeId]
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [contentSize, setContentSize] = useState(0);
  const [containerSize, setContainerSize] = useState(0);

  const isHorizontal = mode === "horizontal";

  // 检测溢出
  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current || !contentRef.current) return;
      const container = containerRef.current;
      const content = contentRef.current;

      let cSize = 0;
      let cntSize = 0;
      let overflow = false;

      if (isHorizontal) {
        cSize = container.clientWidth;
        cntSize = content.scrollWidth;
        overflow = cntSize > cSize;
      } else {
        cSize = container.clientHeight;
        cntSize = content.scrollHeight;
        overflow = cntSize > cSize;
      }

      setContainerSize(cSize);
      setContentSize(cntSize);
      setHasOverflow(overflow);

      // 如果不再溢出，重置 offset
      if (!overflow) {
        setOffset(0);
      }
    };

    const observer = new ResizeObserver(checkOverflow);
    if (containerRef.current) observer.observe(containerRef.current);
    if (contentRef.current) observer.observe(contentRef.current);

    checkOverflow();
    return () => observer.disconnect();
  }, [isHorizontal]);

  const prevDisabled = offset <= 0;
  const nextDisabled = !hasOverflow || offset >= contentSize - containerSize;

  const handlePrev = useCallback(() => {
    if (prevDisabled) return;
    const step = isHorizontal
      ? containerRef.current?.clientWidth ?? 200
      : containerRef.current?.clientHeight ?? 200;
    setOffset((prev) => Math.max(0, prev - step));
  }, [isHorizontal, prevDisabled]);

  const handleNext = useCallback(() => {
    if (nextDisabled) return;
    const step = isHorizontal
      ? containerRef.current?.clientWidth ?? 200
      : containerRef.current?.clientHeight ?? 200;
    const maxOffset = isHorizontal
      ? (contentRef.current?.scrollWidth ?? 0) -
        (containerRef.current?.clientWidth ?? 0)
      : (contentRef.current?.scrollHeight ?? 0) -
        (containerRef.current?.clientHeight ?? 0);
    setOffset((prev) => Math.min(maxOffset, prev + step));
  }, [isHorizontal, nextDisabled]);

  const PrevIcon = arrowIconPrev || (isHorizontal ? <LeftOutlined /> : <UpOutlined />);
  const NextIcon = arrowIconNext || (isHorizontal ? <RightOutlined /> : <DownOutlined />);

  const renderArrowButton = (
    icon: React.ReactNode,
    disabled: boolean,
    onClick: () => void
  ) => {
    const resolvedStyle = semanticStyle
      ? resolvePaginationArrowButtonStyle(semanticStyle, disabled)
      : flatArrowStyle ?? {};
    const iconStyle = semanticStyle
      ? resolvePaginationArrowIconStyle(semanticStyle, disabled)
      : undefined;

    return (
      <Button
        type="text"
        size="middle"
        className="nuwa-menu-page-arrow"
        icon={wrapPaginationArrowIcon(icon, iconStyle)}
        shape="circle"
        disabled={disabled}
        onClick={onClick}
        style={{
          flexShrink: 0,
          alignSelf: "center",
          margin: isHorizontal ? "0 4px" : "4px 0",
          ...resolvedStyle,
          borderRadius: "50%", // 确保圆形，覆盖 root 中的 borderRadius
        }}
      />
    );
  };

  return (
    <div
      data-and-menu-id={styleScopeId}
      style={{
        display: "flex",
        flexDirection: isHorizontal ? "row" : "column",
        alignItems: "center",
        width: "100%",
        height: "100%",
      }}
    >
      {hoverCss ? <style>{hoverCss}</style> : null}
      {renderArrowButton(PrevIcon, prevDisabled, handlePrev)}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflow: "hidden",
          position: "relative",
          display: "flex",
          justifyContent: isHorizontal && !hasOverflow ? "center" : "flex-start",
        }}
      >
        <div
          ref={contentRef}
          style={{
            transform: isHorizontal
              ? `translateX(-${offset}px)`
              : `translateY(-${offset}px)`,
            transition: "transform 0.3s ease-out",
            display: isHorizontal ? "inline-flex" : "block",
            whiteSpace: isHorizontal ? "nowrap" : "normal",
          }}
        >
          {children}
        </div>
      </div>
      {renderArrowButton(NextIcon, nextDisabled, handleNext)}
    </div>
  );
};



/**
 * 滚动容器组件 - 用于实现滚动条模式的溢出处理
 */
const ScrollWrapper: React.FC<{
  mode: "horizontal" | "vertical" | "inline";
  children: React.ReactNode;
}> = ({ mode, children }) => {
  const isHorizontal = mode === "horizontal";

  return (
    <div
      style={{
        overflow: isHorizontal ? "auto hidden" : "hidden auto",
        width: "100%",
        height: "100%",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
      }}
    >
      {children}
    </div>
  );
};

/**
 * SubMenu 标题行（.ant-menu-submenu-title）的样式配置
 * 这是对 Ant Design 原生 API 的扩展，用于解决 SubMenu 标题行无法通过 styles prop 设置的问题
 */
export interface SubMenuRootStyles {
  /** SubMenu 标题行容器样式（边框、padding、borderRadius 等） */
  item?: React.CSSProperties;
  /** SubMenu 标题行图标样式 */
  itemIcon?: React.CSSProperties;
  /** SubMenu 标题行文字样式（字体大小、行高等） */
  itemContent?: React.CSSProperties;
  /** 子级选中时 SubMenu 标题行的样式（背景色、文字颜色等） */
  selected?: React.CSSProperties;
}

/**
 * Semantic DOM 样式配置
 * 参考 Ant Design 6.x Semantic DOM 规范，并扩展 subMenu.root 用于 SubMenu 标题行
 */
export interface MenuSemanticStyles {
  root?: React.CSSProperties;
  item?: React.CSSProperties;
  itemIcon?: React.CSSProperties;
  itemContent?: React.CSSProperties;
  popup?: React.CSSProperties;
  subMenu?: {
    /** 扩展：SubMenu 标题行样式配置（Ant Design 原生不支持，通过 CSS 注入实现） */
    root?: SubMenuRootStyles;
    /** Ant Design 原生：子菜单面板标题样式 */
    itemTitle?: React.CSSProperties;
    /** Ant Design 原生：子菜单列表样式 */
    list?: React.CSSProperties;
    /** Ant Design 原生：子菜单内的项样式 */
    item?: React.CSSProperties;
    /** Ant Design 原生：子菜单内的项图标样式 */
    itemIcon?: React.CSSProperties;
    /** Ant Design 原生：子菜单内的项文字样式 */
    itemContent?: React.CSSProperties;
  };
}

/**
 * Menu Design Token
 * 参考 Ant Design 6.x Design Token 规范
 */
export interface MenuToken {
  colorBgContainer?: string;
  itemBg?: string;
  itemColor?: string;
  itemHoverBg?: string;
  itemHoverColor?: string;
  itemSelectedBg?: string;
  itemSelectedColor?: string;
  itemActiveBg?: string;
  subMenuItemBg?: string;
  darkItemBg?: string;
  darkItemColor?: string;
  darkItemHoverBg?: string;
  darkItemHoverColor?: string;
  darkItemSelectedBg?: string;
  darkItemSelectedColor?: string;
  iconSize?: number;
  iconMarginInlineEnd?: number;
  collapsedWidth?: number;
  collapsedIconSize?: number;
}

export interface AndMenuProps
  extends Omit<
      AntMenuProps,
      "onClick" | "onSelect" | "onOpenChange" | "onDeselect"
    >,
    BaseComponentProps,
    DataSetConfig {
  // 事件 action 配置
  clickAction?: string;
  selectAction?: string;
  openChangeAction?: string;
  deselectAction?: string;

  // 事件回调
  onClick?: (info: any) => void;
  onSelect?: (info: any) => void;
  onOpenChange?: (openKeys: string[]) => void;
  onDeselect?: (info: any) => void;

  // Semantic DOM 支持（classNames 继承 AntMenuProps，与 antd 6 Menu 语义一致）
  styles?: MenuSemanticStyles;

  // Design Token 支持
  token?: MenuToken;

  // 额外属性
  expandIcon?:
    | React.ReactNode
    | ((props: { isOpen?: boolean; isSubMenu?: boolean }) => React.ReactNode);
  overflowedIndicator?: React.ReactNode;

  // openKeys 数据集绑定
  openKeysDataSet?: string;
  openKeysDataSetField?: string;

  // 自定义属性（不传递给 Menu）
  remoteUrl?: string;
  /** 展开触发方式（设计器/业务用，不传给 DOM） */
  expandTrigger?: "click" | "hover" | "iconOnly";

  // 溢出处理配置
  overflowStrategy?: OverflowStrategy;
  paginationArrowStyle?: PaginationArrowStyle;
  paginationArrowIconPrev?: React.ReactNode | string;
  paginationArrowIconNext?: React.ReactNode | string;

  /** 迭代器 / Masonry 行号；与 Extend `setProps` 作用域 id（deriveScopedComponentId）一致 */
  index?: number;
}

export const AndMenu: React.FC<AndMenuProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  index,
  clickAction,
  selectAction,
  openChangeAction,
  deselectAction,
  onClick,
  onSelect,
  onOpenChange,
  onDeselect,
  items,
  selectedKeys: propSelectedKeys,
  openKeys: propOpenKeys,
  defaultOpenKeys,
  styles,
  classNames,
  token,
  expandIcon,
  overflowedIndicator,
  openKeysDataSet,
  openKeysDataSetField,
  mode,
  inlineCollapsed,
  remoteUrl: _remoteUrl,
  overflowStrategy = "default",
  paginationArrowStyle,
  paginationArrowIconPrev,
  paginationArrowIconNext,
  expandTrigger: _expandTrigger, // 仅设计器/业务用，不传给 DOM，避免 React 警告
  ...restProps
}) => {
  const ctx = useContext(DataSetInheritContext);
  const derivedPropsComponentId = useMemo(
    () => (sessionId && id ? deriveScopedComponentId(id, ctx, index) : null),
    [sessionId, id, ctx?.parentIndex, ctx?.index, index]
  );
  const storePropsOverride = useAndStore((state) =>
    derivedPropsComponentId && sessionId
      ? state.propsStore[`${sessionId}_props`]?.[derivedPropsComponentId] ?? null
      : null
  );

  const addCommand = useAndStore((state) => state.addCommand);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );
  // 订阅完整的 store 对象
  // 使用 shallow 比较以避免因返回新对象导致的无限重渲染
  const storeValue = useAndStore(
    (state) => {
      if (dataSetKey) {
        // 如果指定了字段，获取特定字段
        if (dataSetField) {
          return state.getDataSet(dataSetKey, dataSetField);
        }
        // 未指定字段时获取整个数据集对象（key 已含 sessionId 前缀）
        return state.dataSets[dataSetKey];
      }
      return undefined;
    },
    // 使用 shallow 比较确保只有通过内容变更才触发更新
    // 如果找不到依赖包，可以自定义一个简单的浅比较函数或暂时使用 strict equality (默认)
    // 但鉴于 dataSetField 返回的可能是新引用的对象，必须谨慎。
    // 更安全的做法是分别订阅需要的字段，或者确保 getDataSet 返回的是稳定的 state 引用。
    // 这里我们先假定 state.dataSets[...] 返回的是稳定的 immer state 引用。
    // 为了防止 undefined 情况下的 {} 导致的新引用，我们在 selector 内部只返回 undefined，在外部处理默认值。
  );

  // 默认值对象，保持引用稳定；仅当 storeValue 为 undefined/null 时使用，避免空数组 [] 被当作无数据
  const emptyObject = useMemo(() => ({}), []);
  const safeStoreValue =
    storeValue !== undefined && storeValue !== null ? storeValue : emptyObject;

  // 从 store 派生菜单项，兼容多种写入约定：
  // 1) { items: MenuItem[] } — 与 selectedKeys/openKeys 同源
  // 2) MenuItem[] — dataSetField 指向数组时 getDataSet 直接返回数组
  // 3) { value: MenuItem[] } 或数组 — 只配 dataSet 未配 dataSetField 时，Extend 常用 setDataSet(key, '', list)
  const storeDerivedItems = useMemo(
    () =>
      safeStoreValue?.items ??
      (Array.isArray(safeStoreValue) ? safeStoreValue : undefined) ??
      (Array.isArray(safeStoreValue?.value) ? safeStoreValue.value : undefined),
    [safeStoreValue]
  );
  // 设计器模式(develop)：优先 store，无则回退到静态 items 便于画布预览；运行时且配置了 dataSet：仅用 store，不回退到静态 items
  const finalItems = useMemo(() => {
    if (develop) {
      return storeDerivedItems ?? items ?? [];
    }
    if (dataSetKey) {
      return storeDerivedItems ?? [];
    }
    return items ?? [];
  }, [develop, dataSetKey, storeDerivedItems, items]);
  
  // selectedKeys（propsStore：Extend.setProps，无 dataSet 时仍可受控）
  const finalSelectedKeys = useMemo(
    () =>
      storePropsOverride?.selectedKeys ??
      safeStoreValue.selectedKeys ??
      propSelectedKeys,
    [storePropsOverride?.selectedKeys, safeStoreValue.selectedKeys, propSelectedKeys]
  );

  // openKeys（openKeysDataSet 和 openKeysDataSetField 依然保留向后兼容，但优先使用 storeValue）
  const finalOpenKeys = useMemo(
    () =>
      storePropsOverride?.openKeys ??
      safeStoreValue.openKeys ??
      (openKeysDataSet && openKeysDataSetField ? undefined : propOpenKeys),
    [
      storePropsOverride?.openKeys,
      safeStoreValue.openKeys,
      openKeysDataSet,
      openKeysDataSetField,
      propOpenKeys,
    ]
  );
  
  // 保持向后兼容：如果配置了 openKeysDataSet，依然尝试从那里获取
  // 注意：如果 storeValue.openKeys 存在，上面的 finalOpenKeys 已经拿到了，所以这里主要是为了旧配置
  const openKeysDataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, openKeysDataSet),
    [sessionId, openKeysDataSet]
  );
  const legacyStoreOpenKeys = useAndStore((state) => {
    if (!safeStoreValue.openKeys && openKeysDataSetKey && openKeysDataSetField) {
      return state.getDataSet(openKeysDataSetKey, openKeysDataSetField);
    }
    return undefined;
  });

  const effectiveItems = useMemo(() => {
    const process = (list: any[]): any[] => {
      if (!Array.isArray(list)) return [];
      return list.map((item) => {
        const newItem = { ...item };
        if (newItem.icon && typeof newItem.icon === "string") {
          const IconComp = (Icons as any)[newItem.icon];
          if (IconComp) {
            newItem.icon = <IconComp />;
          }
        }
        if (newItem.children && newItem.children.length > 0) {
          newItem.children = process(newItem.children);
        }
        return newItem;
      });
    };
    return process(finalItems || []);
  }, [finalItems]);

  const effectiveSelectedKeys = useMemo(
    () => finalSelectedKeys,
    [finalSelectedKeys]
  );

  const effectiveOpenKeys = useMemo(
    () => finalOpenKeys ?? legacyStoreOpenKeys,
    [finalOpenKeys, legacyStoreOpenKeys]
  );

  // 点击事件
  const handleClick = useCallback(
    (info: any) => {
      if (onClick) {
        onClick(info);
        return;
      }
      if (clickAction && sessionId) {
        addCommand(
          sessionId,
          createCommand(clickAction, { key: info.key, keyPath: info.keyPath })
        );
      }
    },
    [onClick, clickAction, sessionId, addCommand]
  );

  // 选中事件
  const handleSelect = useCallback(
    (info: any) => {
      if (onSelect) {
        onSelect(info);
        return;
      }
      
      // 更新 store（统一使用 dataSetKey，避免命名冲突）
      if (dataSetKey) {
        if (dataSetField) {
           const currentState = useAndStore.getState().getDataSet(dataSetKey, dataSetField) || {};
           setDataSet(dataSetKey, dataSetField, { ...currentState, selectedKeys: info.selectedKeys });
        } else {
           setDataSet(dataSetKey, 'selectedKeys', info.selectedKeys);
        }
      }
      
      if (selectAction && sessionId) {
        addCommand(
          sessionId,
          createCommand(selectAction, { selectedKeys: info.selectedKeys })
        );
      }
    },
    [
      onSelect,
      dataSetKey,
      dataSetField,
      setDataSet,
      selectAction,
      sessionId,
      addCommand,
    ]
  );

  // 展开/收起事件
  const handleOpenChange = useCallback(
    (keys: string[]) => {
      if (onOpenChange) {
        onOpenChange(keys);
        return;
      }
      
      // 更新 store（统一使用 dataSetKey / openKeysDataSetKey）
      if (dataSetKey) {
        if (dataSetField) {
           const currentState = useAndStore.getState().getDataSet(dataSetKey, dataSetField) || {};
           setDataSet(dataSetKey, dataSetField, { ...currentState, openKeys: keys });
        } else {
           setDataSet(dataSetKey, 'openKeys', keys);
        }
      } else if (openKeysDataSetKey && openKeysDataSetField) {
         setDataSet(openKeysDataSetKey, openKeysDataSetField, keys);
      }
      
      if (openChangeAction && sessionId) {
        addCommand(
          sessionId,
          createCommand(openChangeAction, { openKeys: keys })
        );
      }
    },
    [
      onOpenChange,
      dataSetKey,
      dataSetField,
      openKeysDataSetKey,
      openKeysDataSetField,
      setDataSet,
      openChangeAction,
      sessionId,
      addCommand,
    ]
  );

  // 取消选中事件
  const handleDeselect = useCallback(
    (info: any) => {
      if (onDeselect) {
        onDeselect(info);
        return;
      }
      if (deselectAction && sessionId) {
        addCommand(
          sessionId,
          createCommand(deselectAction, {
            selectedKeys: info.selectedKeys,
            key: info.key,
          })
        );
      }
    },
    [onDeselect, deselectAction, sessionId, addCommand]
  );



  // inlineCollapsed 只在 inline 模式下有效
  const inlineProps =
    mode === "inline" && inlineCollapsed !== undefined
      ? { inlineCollapsed }
      : {};

  // 过滤空值的 token
  const filteredToken = useMemo(() => {
    if (!token) return undefined;
    const result: Record<string, any> = {};

    Object.entries(token).forEach(([key, value]) => {
      if (value !== "" && value !== undefined && value !== null) {
        result[key] = value;
      }
    });

    return Object.keys(result).length > 0 ? result : undefined;
  }, [token]);

  /** 去掉 Menu classNames 中空字符串/空嵌套对象，避免无意义 class 片段 */
  const filteredClassNames = useMemo((): AntMenuProps["classNames"] | undefined => {
    if (classNames == null || typeof classNames === "function") return classNames;
    const clean = (val: unknown): unknown => {
      if (val === undefined || val === null || val === "") return undefined;
      if (typeof val === "string") return val;
      if (typeof val !== "object" || Array.isArray(val)) return val;
      const o: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
        const c = clean(v);
        if (c !== undefined) o[k] = c;
      }
      return Object.keys(o).length ? o : undefined;
    };
    const cleaned = clean(classNames);
    return cleaned as AntMenuProps["classNames"] | undefined;
  }, [classNames]);

  // 在 develop 模式下禁用动画，避免 GrapesJS iframe 中的 CSSMotion 错误
  // 设置 motionName 为空字符串来禁用动画效果
  const motionProps = develop
    ? {
        motion: { motionName: "" } as any,
      }
    : {};

  // 处理翻页箭头图标（支持字符串转换为组件）
  const processedArrowIconPrev = useMemo(() => {
    if (typeof paginationArrowIconPrev === "string" && paginationArrowIconPrev) {
      const IconComp = (Icons as any)[paginationArrowIconPrev];
      if (IconComp) return <IconComp />;
    }
    return paginationArrowIconPrev;
  }, [paginationArrowIconPrev]);

  const processedArrowIconNext = useMemo(() => {
    if (typeof paginationArrowIconNext === "string" && paginationArrowIconNext) {
      const IconComp = (Icons as any)[paginationArrowIconNext];
      if (IconComp) return <IconComp />;
    }
    return paginationArrowIconNext;
  }, [paginationArrowIconNext]);

  // 根据溢出策略决定是否隐藏原生省略号
  const effectiveOverflowedIndicator = useMemo(() => {
    if (overflowStrategy === "pagination" || overflowStrategy === "scroll") {
      return null; // 隐藏原生省略号
    }
    return overflowedIndicator;
  }, [overflowStrategy, overflowedIndicator]);

  // 生成唯一 ID 用于 CSS 选择器作用域
  const reactId = useId();
  const menuScopeId = id || reactId.replace(/:/g, '-');

  /**
   * 生成 SubMenu 标题行的 CSS 样式。
   * 从 styles.subMenu.root 读取配置，通过 CSS 注入应用到 SubMenu 标题行。
   * 这是对 Ant Design 原生 API 的扩展，解决 SubMenu 标题行无法通过 styles prop 设置的问题。
   */
  const subMenuTitleCss = useMemo(() => {
    if (!styles || typeof styles === "function") return '';
    
    // 从 subMenu.root 读取配置
    const rootConfig = styles.subMenu?.root;
    if (!rootConfig) return '';
    
    const cssRules: string[] = [];
    const scope = `[data-and-menu-id="${menuScopeId}"]`;
    
    // 1. subMenu.root.item → .ant-menu-submenu-title（边框、padding、borderRadius 等）
    if (rootConfig.item && Object.keys(rootConfig.item).length > 0) {
      const cssStr = cssPropertiesToString(rootConfig.item);
      cssRules.push(`${scope} .ant-menu-submenu > .ant-menu-submenu-title { ${cssStr} }`);
    }
    
    // 2. subMenu.root.itemIcon → .ant-menu-submenu-title .ant-menu-item-icon
    if (rootConfig.itemIcon && Object.keys(rootConfig.itemIcon).length > 0) {
      const cssStr = cssPropertiesToString(rootConfig.itemIcon);
      cssRules.push(`${scope} .ant-menu-submenu > .ant-menu-submenu-title .ant-menu-item-icon { ${cssStr} }`);
    }
    
    // 3. subMenu.root.itemContent → .ant-menu-submenu-title .ant-menu-title-content（字体大小等）
    if (rootConfig.itemContent && Object.keys(rootConfig.itemContent).length > 0) {
      const cssStr = cssPropertiesToString(rootConfig.itemContent);
      cssRules.push(`${scope} .ant-menu-submenu > .ant-menu-submenu-title .ant-menu-title-content { ${cssStr} }`);
    }
    
    // 4. subMenu.root.selected → .ant-menu-submenu-selected > .ant-menu-submenu-title（子级选中时的样式）
    if (rootConfig.selected && Object.keys(rootConfig.selected).length > 0) {
      const cssStr = cssPropertiesToString(rootConfig.selected);
      cssRules.push(`${scope} .ant-menu-submenu-selected > .ant-menu-submenu-title { ${cssStr} }`);
    }
    
    return cssRules.join('\n');
  }, [styles, menuScopeId]);

  const menuElement = (
    <Menu
      {...restProps}
      {...inlineProps}
      {...motionProps}
      mode={mode}
      items={effectiveItems}
      selectedKeys={effectiveSelectedKeys}
      openKeys={effectiveOpenKeys}
      defaultOpenKeys={defaultOpenKeys}
      onClick={handleClick}
      onSelect={handleSelect}
      onOpenChange={handleOpenChange}
      onDeselect={handleDeselect}
      styles={styles}
      classNames={filteredClassNames}
      expandIcon={expandIcon}
      overflowedIndicator={effectiveOverflowedIndicator}
      disabledOverflow={overflowStrategy !== "default"}
    />
  );

  // 根据溢出策略包装菜单
  const renderMenuWithOverflow = () => {
    if (overflowStrategy === "pagination") {
      return (
        <PaginationWrapper
          mode={mode || "horizontal"}
          arrowStyle={paginationArrowStyle}
          styleScopeId={menuScopeId}
          arrowIconPrev={processedArrowIconPrev}
          arrowIconNext={processedArrowIconNext}
        >
          {menuElement}
        </PaginationWrapper>
      );
    }

    if (overflowStrategy === "scroll") {
      return (
        <ScrollWrapper mode={mode || "horizontal"}>
          {menuElement}
        </ScrollWrapper>
      );
    }

    return menuElement;
  };

  // 构建 theme 配置
  const themeConfig = useMemo(() => {
    const config: any = {};

    // develop 模式下禁用动画
    if (develop) {
      config.motion = false;
    }

    // 添加 token 配置
    if (filteredToken) {
      config.components = { Menu: filteredToken };
    }

    return Object.keys(config).length > 0 ? config : undefined;
  }, [develop, filteredToken]);

  // 渲染最终内容，包含 CSS 注入和作用域 wrapper
  const renderContent = () => {
    const menuContent = renderMenuWithOverflow();
    
    // 如果有 subMenu 标题样式需要注入，用 wrapper 包裹
    const wrappedContent = subMenuTitleCss ? (
      <div data-and-menu-id={menuScopeId} style={{ display: 'contents' }}>
        <style>{subMenuTitleCss}</style>
        {menuContent}
      </div>
    ) : menuContent;

    // 如果有 theme 配置，用 ConfigProvider 包裹
    if (themeConfig) {
      return <ConfigProvider theme={themeConfig}>{wrappedContent}</ConfigProvider>;
    }

    return wrappedContent;
  };

  return renderContent();
};

export default AndMenu;

